import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Loader2, ChevronDown } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { COMPANY, FAQS } from "@/lib/data";
import { useBooking } from "@/context/BookingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { openBooking } = useBooking();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    if (!agreed) {
      toast.error("Please agree to the Terms & Conditions and Privacy Policy.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We'll reach out shortly.");
      setForm({ name: "", email: "", phone: "" });
      setAgreed(false);
    } catch {
      toast.error("Couldn't send. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-3 text-white placeholder:text-white/30 rounded-none transition-colors";

  const contactItems = [
    { icon: Phone, label: "Call / Text", value: COMPANY.phone, href: COMPANY.phoneHref },
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: MapPin, label: "Location", value: COMPANY.address, href: "#" },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 border-t border-white/5 bg-[#080808]">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* Left: info + FAQ */}
        <div>
          <span className="text-xs md:text-sm tracking-[0.25em] uppercase text-[#F2B705] font-bold">Get In Touch</span>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mt-3 leading-[0.95]">
            Let's Get You <span className="text-gold-gradient">Rolling.</span>
          </h2>
          <p className="text-white/55 mt-4 text-lg font-light">
            Questions, custom requests, or ready to book? Reach out — we answer fast.
          </p>

          <div className="mt-8 space-y-3">
            {contactItems.map((c) => (
              <a
                key={c.label}
                href={c.href}
                data-testid={`contact-info-${c.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className="flex items-center gap-4 bg-[#0C0C0C] border border-white/10 rounded-lg p-4 hover:border-[#F2B705]/40 transition-colors group"
              >
                <span className="w-11 h-11 rounded-lg bg-[#F2B705]/10 flex items-center justify-center group-hover:bg-[#F2B705] transition-colors">
                  <c.icon size={20} className="text-[#F2B705] group-hover:text-black transition-colors" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/40">{c.label}</div>
                  <div className="font-semibold">{c.value}</div>
                </div>
              </a>
            ))}
          </div>

          <button
            data-testid="contact-book-now"
            onClick={() => openBooking("")}
            className="mt-6 bg-[#F2B705] text-black font-bold px-8 py-4 rounded-md hover:bg-white transition-colors duration-300"
          >
            Request a Reservation
          </button>

          <div className="mt-12 scroll-mt-24" id="faq" data-testid="faq-section">
            <h3 className="font-display font-bold text-xl mb-3">FAQs</h3>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left hover:text-[#F2B705] hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-white/55 font-light">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        {/* Right: contact form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-[#0C0C0C] border border-white/10 rounded-2xl p-8 md:p-10 h-fit"
        >
          <h3 className="font-display font-bold text-2xl">Contact Us</h3>
          <p className="text-white/50 text-sm mt-1">Drop your details and we'll get right back to you.</p>

          {/* Legal links right above the contact box */}
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <Link to="/terms" data-testid="contact-terms-link" className="text-[#F2B705] hover:underline">Terms &amp; Conditions</Link>
            <span className="text-white/20">·</span>
            <Link to="/privacy" data-testid="contact-privacy-link" className="text-[#F2B705] hover:underline">Privacy Policy</Link>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-6" data-testid="contact-form">
            <input data-testid="contact-name" className={inputCls} placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)} />
            <input data-testid="contact-email" type="email" className={inputCls} placeholder="Your email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            <input data-testid="contact-phone" type="tel" className={inputCls} placeholder="Your phone number" value={form.phone} onChange={(e) => set("phone", e.target.value)} />

            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                data-testid="contact-agree-checkbox"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#F2B705] shrink-0"
              />
              <span className="text-sm text-white/55">
                I agree to the{" "}
                <Link to="/terms" className="text-[#F2B705] hover:underline">Terms &amp; Conditions</Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-[#F2B705] hover:underline">Privacy Policy</Link>, and consent to be contacted by phone, SMS and email.
              </span>
            </label>

            <button
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#F2B705] text-black font-bold py-3.5 rounded-md hover:bg-white transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="animate-spin" size={18} />}
              {loading ? "Sending..." : "Request a Callback"}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
